import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight } from "lucide-react";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";
import { useCmsProducts } from "@/hooks/use-cms";
import { FadeUp, ScaleIn } from "@/components/ui/scroll-animations";
import { getProductUrl } from "@/utils/category-routes";

const FeaturedProducts = () => {
  const { data: allProducts, loading } = useCmsProducts();

  const featuredProducts = allProducts
    .filter((p) => p.category === "ionizers" || p.category === "hydrogen")
    .slice(0, 6);

  if (loading || featuredProducts.length === 0) return null;

  return (
    <section className="section-padding bg-secondary">
      <div className="container-premium">
        <FadeUp className="text-center mb-16">
          <span className="inline-block text-accent font-medium text-sm tracking-wide uppercase mb-4">PurePro® USA</span>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-serif font-bold text-primary mb-6">Best Water Ionizers in India</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">Discover our range of world-class water ionizers and hydrogen products.</p>
        </FadeUp>

        <ScaleIn>
          <Carousel opts={{ align: "start", loop: true }} className="w-full">
            <CarouselContent className="-ml-4">
              {featuredProducts.map((product) => (
                <CarouselItem key={product.id} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="group h-full hover-lift border-0 shadow-elegant bg-card overflow-hidden relative">
                    <CardContent className="p-0">
                      <div className="aspect-square bg-gradient-to-br from-secondary to-muted flex items-center justify-center relative overflow-hidden">
                        <img src={product.image || ""} alt={`${product.name} — Best ${product.category === "ionizers" ? "water ionizer" : "hydrogen water product"} India by PurePro`} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                        {product.price && (
                          <div className="absolute top-4 right-4 bg-accent text-accent-foreground px-3 py-1 rounded-full text-xs font-semibold">{product.price}</div>
                        )}
                        <div className="absolute inset-0 bg-primary/80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                          <Button asChild variant="secondary" className="btn-cyan border-0">
                            <Link to={getProductUrl(product.id)}>View Details <ArrowRight className="w-4 h-4 ml-2" /></Link>
                          </Button>
                        </div>
                      </div>
                      <div className="p-6">
                        <span className="text-accent text-xs font-semibold tracking-wide uppercase">{product.short_name || product.category}</span>
                        <h3 className="text-xl font-serif font-bold text-primary mt-2 mb-3">{product.name}</h3>
                        <p className="text-muted-foreground text-sm mb-4 line-clamp-2">{product.description}</p>
                        <div className="flex flex-wrap gap-2">
                          {(product.features || []).slice(0, 3).map((f) => (
                            <span key={f} className="text-xs bg-secondary text-secondary-foreground px-3 py-1 rounded-full font-medium">{f}</span>
                          ))}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <CarouselPrevious className="hidden md:flex -left-4 bg-card border-border hover:bg-accent hover:text-accent-foreground transition-all duration-300" />
            <CarouselNext className="hidden md:flex -right-4 bg-card border-border hover:bg-accent hover:text-accent-foreground transition-all duration-300" />
          </Carousel>
        </ScaleIn>

        <FadeUp delay={0.2} className="text-center mt-12">
          <Button asChild className="btn-ocean border-0 h-12 px-8">
            <Link to="/products">View All Products <ArrowRight className="w-5 h-5 ml-2" /></Link>
          </Button>
        </FadeUp>
      </div>
    </section>
  );
};

export default FeaturedProducts;
